import { ApiError } from '@/api/error'
import { getLanguageHeader } from '@/i18n'
import { getDestinationHeaderValue, getDestinationQueryParamIfNeeded } from '@/utils/api_destination_header'

export type OptionalKey<T> = Exclude<
  {
    [Key in keyof T]: T extends Record<Key, T[Key]> ? never : Key
  }[keyof T],
  undefined
>

export type DefaultsOf<T> = {
  [Key in OptionalKey<Exclude<T, undefined>>]: Exclude<T, undefined>[Key]
}

export type ApiHeaders = Record<string, string | number | undefined>


export type ErrorCallback = (error: ApiError) => any

interface ApiContext {
  readonly url: string
  readonly defaultHeaders?: ApiHeaders
  readonly errorCallback?: ErrorCallback
}

type HttpMethod = 'GET' | 'POST'

function filterValidHeaders(headers: any) {
  const newHeaders: any = {}
  for (const name of Object.keys(headers)) {
    let value = headers[name]
    if (typeof value === 'number') value = value.toString()
    if (typeof value !== 'string' || !value.length) continue // eslint-disable-line no-continue
    newHeaders[name] = value
  }
  return newHeaders
}

function buildSearchParams(body: object): URLSearchParams {
  const params: Record<string, string> = {}
  for (const [k, v] of Object.entries(body)) {
    params[k] = v.toString()
  }
  return new URLSearchParams(params)
}

function requestTemplate(
  context: ApiContext,
  method: HttpMethod,
  defaultParam?: {
    body?: object
    headers?: ApiHeaders
  }
): (body?: object, headers?: ApiHeaders) => Promise<Response> {
  return (body?: object, headers?: ApiHeaders): Promise<Response> => {
    const init: RequestInit = { method }
    let url = context.url

    if (method === 'GET' && body) {
      const params = buildSearchParams(body)
      url += `?${params.toString()}`
    } else {
      if (body || (defaultParam && defaultParam.body)) {
        init.body = JSON.stringify({ ...(defaultParam || {}).body, ...body })
      }
    }

    // 向き先のdestinationはquery paramで制御
    // 理由：PreFlight(OPTIONS)リクエストのheaderは開発者側で操作できないので
    url += getDestinationQueryParamIfNeeded(url, getDestinationHeaderValue())

    init.headers = filterValidHeaders({
      ...context.defaultHeaders,
      ...getLanguageHeader(),
      ...(defaultParam || {}).headers,
      ...headers,
    })
    return fetch(url, init)
  }
}

function genApi(method: HttpMethod) {
  function requestByMethod<ApiResult = void>(): () => Promise<ApiResult>

  function requestByMethod<ApiBody, ApiResult>(
    defaultBody?: DefaultsOf<ApiBody>
  ): (param: ApiBody) => Promise<ApiResult>

  function requestByMethod<
    ApiBody,
    ApiHeaders extends { [name: string]: string | number },
    ApiResult
  >(
    defaultBody?: DefaultsOf<ApiBody>,
    defaultHeaders?: DefaultsOf<ApiHeaders>
  ): (body: ApiBody, headers: ApiHeaders) => Promise<ApiResult>

  function requestByMethod(defaultParam?: {
    body?: object
    headers?: ApiHeaders
  }) {
    async function request(
      this: ApiContext,
      body?: object,
      headers?: ApiHeaders
    ) {
      try {
        const template = requestTemplate(this, method, defaultParam)
        const response = await template(body, headers)
        const content = await response.json()
        if (!response.ok) {
          let errorCode = 0
          if (content && content.payload.errorCode) {
            errorCode = content.payload.errorCode
          }

          throw new ApiError(
            response.statusText,
            response.status,
            this.url,
            errorCode
          )
        }

        return content.payload
      } catch (error) {
        if (this.errorCallback) this.errorCallback(error as ApiError)
        throw error
      }
    }
    return request
  }
  return requestByMethod
}

type Decoder<T> = {
  decode: (input: Uint8Array) => T
}

function genApiV2(method: HttpMethod) {
  function requestByMethod<ApiResult = void>(decoder: {
    decode: (input: Uint8Array) => ApiResult
  }): () => Promise<ApiResult>

  function requestByMethod<ApiBody, ApiResult>(
    decoder: Decoder<ApiResult>,
    defaultBody?: DefaultsOf<ApiBody>
  ): (param: ApiBody) => Promise<ApiResult>

  function requestByMethod<
    ApiBody,
    ApiHeaders extends { [name: string]: string | number },
    ApiResult
  >(
    decoder: Decoder<ApiResult>,
    defaultBody?: DefaultsOf<ApiBody>,
    defaultHeaders?: DefaultsOf<ApiHeaders>
  ): (body: ApiBody, headers: ApiHeaders) => Promise<ApiResult>

  function requestByMethod<ApiResult>(
    decoder: Decoder<ApiResult>,
    defaultParam?: {
      body?: object
      headers?: ApiHeaders
    }
  ) {
    async function request(
      this: ApiContext,
      body?: object,
      headers?: ApiHeaders
    ): Promise<ApiResult> {
      try {
        const template = requestTemplate(this, method, defaultParam)
        const response = await template(body, headers)
        if (!response.ok)
          throw new ApiError(response.statusText, response.status, this.url)

        // statusがokならば、protobufとして処理して良い
        const buffer = await response.arrayBuffer()
        return decoder.decode(new Uint8Array(buffer))
      } catch (error) {
        if (this.errorCallback) this.errorCallback(error as ApiError)
        throw error
      }
    }
    return request
  }
  return requestByMethod
}

export const get = genApi('GET')
export const getV2 = genApiV2('GET')
export const post = genApi('POST')
export const postV2 = genApiV2('POST')

function snakeCase(str: string) {
  return str.replace(/[A-Z]/g, (s) => `_${s.charAt(0).toLowerCase()}`)
}

function routePath<T>(
  tree: T,
  parentPaths: string[],
  defaultHeaders?: ApiHeaders,
  errorCallback?: ErrorCallback
) {
  if (tree instanceof Function) {
    const context: ApiContext = {
      url: parentPaths.filter((path) => path !== '_').join('/'),
      defaultHeaders,
      errorCallback,
    }
    return tree.bind(context) as T
  }
  const root = {} as T
  for (const path of Object.keys(tree as {}) as (keyof T)[]) {
    const pathObj = tree[path]
    root[path] = routePath(
      pathObj,
      parentPaths.concat(snakeCase(path as string)),
      defaultHeaders,
      errorCallback
    )
  }
  return root
}

export function apiRoutes(
  prefixPath: string,
  defaultHeaders?: ApiHeaders,
  errorCallback?: ErrorCallback
) {
  return function rootRoutePath<T>(tree: T) {
    return routePath(tree, [prefixPath], defaultHeaders, errorCallback)
  }
}
